import {
  createContext, useState, useEffect, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import API_BASE_URL from '../config/api';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        // Parse defensively: the literal strings "undefined"/"null" (or any
        // corrupt value) must not crash init or wedge `loading` forever.
        let parsedUser = null;
        if (storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
          try {
            parsedUser = JSON.parse(storedUser);
          } catch (parseErr) {
            parsedUser = null;
          }
        }

        if (storedToken && parsedUser) {
          setToken(storedToken);
          // Set stored user initially for fast render
          setUser(parsedUser);

          // Fetch fresh user data from server to sync across devices
          try {
            const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${storedToken}` },
            });
            if (response.ok) {
              const data = await response.json();
              setUser(data.user);
              localStorage.setItem('user', JSON.stringify(data.user));
            } else if (response.status === 401) {
              // Token expired/invalid - clear auth state
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              setToken(null);
              setUser(null);
            }
          } catch (err) {
            // Network error - keep using cached data (silently fail)
          }
        } else if (storedToken || storedUser) {
          // Corrupt/partial auth state - clear it and fall back to logged-out
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const changePassword = useCallback(async (currentPassword, newPassword, confirmPassword) => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to change password');
    }

    // Update token and user with new JWT
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));

    return data;
  }, [token]);

  const updateProfile = useCallback(async ({ fullName, username, appearance } = {}) => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      // Only send what was supplied — the route leaves omitted fields alone,
      // so a username-only change must not blank the full name or reset a
      // character.
      //
      // ⚠️  EVERY FIELD THE CALLER MAY PASS MUST BE NAMED HERE. The destructure
      // above silently discards anything it does not list: `appearance` was
      // missing once, and the result was a save that appeared to succeed while
      // the request never carried it. Nothing errors — the server simply
      // answers a payload it was never sent.
      body: JSON.stringify({ fullName, username, appearance }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update profile');
    }

    setUser(data.user);
    localStorage.setItem('user', JSON.stringify(data.user));

    return data;
  }, [token]);

  const uploadProfilePicture = useCallback(async (base64Image) => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/profile/picture`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ profilePicture: base64Image }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to upload profile picture');
    }

    setUser(data.user);
    localStorage.setItem('user', JSON.stringify(data.user));

    return data;
  }, [token]);

  const removeProfilePicture = useCallback(async () => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/profile/picture`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to remove profile picture');
    }

    setUser(data.user);
    localStorage.setItem('user', JSON.stringify(data.user));

    return data;
  }, [token]);

  const deleteAccount = useCallback(async (confirmEmail) => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/account`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ confirmEmail }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete account');
    }

    // Clear local state and storage
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    return data;
  }, [token]);

  const value = useMemo(() => ({
    user,
    token,
    login,
    logout,
    changePassword,
    updateProfile,
    uploadProfilePicture,
    removeProfilePicture,
    deleteAccount,
    loading,
  }), [
    user, token, loading, changePassword, updateProfile, uploadProfilePicture,
    removeProfilePicture, deleteAccount,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
